import React from 'react';
import {
  type FormattedSequenceItem,
  type FormattedEventItem,
  type FormattedBreakItem,
} from '../../utils/sequential';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  BodySmall,
  SubTitleSmall,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Timeline,
  TimelineItem,
} from '@spaced-out/ui-design-system/lib/components/Timeline';
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';

import css from './sequence-day-card.css';


interface ScheduleDayCardProps {
  daySchedule: Extract<FormattedSequenceItem, {type: 'day'}>;
  isMultiDaySequence: boolean;
}

export const ScheduleDayCard = ({
  daySchedule,
  isMultiDaySequence,
}: ScheduleDayCardProps) => {
  const {t} = useLabelTranslation();
  return (
    <Card testId="sequential-day-card">
      {isMultiDaySequence && (
        <CardHeader>
          <CardTitle>
            <SubTitleSmall>
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {t('Day', 'Day')} {daySchedule.dayNumber}
            </SubTitleSmall>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <Timeline orientation="left">
          {Array.isArray(daySchedule.roundsAndBreaks) &&
            daySchedule.roundsAndBreaks.map((roundOrBreak, index) => {
              const {
                type,
                roundNumber,
                name,
                formattedTimeRange,
                formattedDate,
                location,
              } = roundOrBreak as FormattedEventItem;
              if (type === 'round') {
                return (
                  <TimelineItem
                    key={`round-${roundNumber}-${index}`}
                    iconName={roundNumber.toString()}
                    classNames={{
                      icon: css.timelineItemIcon,
                    }}
                    iconColor="neutral"
                    enableCardWrapper={false}
                    title={
                      <BodyMediumBold>
                        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                        {name}
                      </BodyMediumBold>
                    }
                  >
                    <Card testId="round-event-card">
                      <CardHeader>
                        <CardTitle className={css.timelineCardHeader}>
                          <div className={css.iconWrapper}>
                            <Icon
                              color="tertiary"
                              name="clock"
                              size="small"
                              testId="icon-story"
                              type="solid"
                            />
                            <BodySmall color="secondary">
                              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                              {formattedTimeRange}
                            </BodySmall>
                          </div>
                          <div className={css.iconWrapper}>
                            <Icon
                              color="tertiary"
                              name="calendar"
                              size="small"
                              testId="icon-story"
                              type="solid"
                            />
                            <BodySmall color="secondary">
                              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                              {formattedDate}
                            </BodySmall>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {location && (
                          <div className={css.iconWrapper}>
                            <Icon
                              color="tertiary"
                              name="location-dot"
                              size="small"
                              testId="icon-story"
                              type="solid"
                            />
                            <BodySmall color="secondary">
                              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                              {location}
                            </BodySmall>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TimelineItem>
                );
              } else if (type === 'break') {
                return (
                  <TimelineItem
                    children={null}
                    key={`break-${index}`}
                    iconName="mug-saucer"
                    classNames={{
                      icon: css.timelineItemIcon,
                    }}
                    iconColor="neutral"
                    iconType="solid"
                    enableCardWrapper={false}
                    title={
                      <BodyMediumBold>
                        {(roundOrBreak as FormattedBreakItem).duration && (
                          <BodyMediumBold>
                            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                            {`${t('Break', 'Break')} - ${
                              (roundOrBreak as FormattedBreakItem).duration
                                .amount
                            } ${
                              (roundOrBreak as FormattedBreakItem).duration.unit
                            }`}
                          </BodyMediumBold>
                        )}
                      </BodyMediumBold>
                    }
                    description={
                      <BodySmall color="secondary">
                        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                        {formattedTimeRange}
                      </BodySmall>
                    }
                  />
                );
              }
              return null;
            })}
        </Timeline>
      </CardContent>
    </Card>
  );
};
